import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Form, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';

import { StyledIconButton } from 'common/components/button/StyledButton';
import PositionedModal from 'common/components/PositionedModal';
import { StyledDateTimeInput } from 'common/components/DateTimeInput';
import CronExpressionInput, { validateCronExpression } from 'common/components/CronExpressionInput';
import { FieldLabel, FormRow } from 'common/styles/page';
import { getCurrentOrganization, isCurrentUserStaffOrSuperAdmin } from 'common/selectors/auth';
import { getImpersonationMode, ImpersonationMode } from 'common/slices/impersonationSlice';

import type { AssessmentQuestionSetOption, PulseSurvey } from '../api/pulseSurveyPortalApi';

const ORG_AUDIENCE_VALUE = 'org';
const TEAM_AUDIENCE_PREFIX = 'team:';
const DEFAULT_REMINDER_SCHEDULE = '0 9 */2 * *';

type PulseSurveyFormModalProps = {
  open: boolean;
  survey?: PulseSurvey | null;
  questionSets: AssessmentQuestionSetOption[];
  teams?: Array<{ id: number; name: string }>;
  orgName?: string;
  isSubmitting?: boolean;
  defaultReminderSchedule?: string;
  orgTimezone?: string;
  onClose: () => void;
  onSubmit: (request: PulseSurveyFormSubmitRequest) => Promise<void>;
};

export type PulseSurveyFormSubmitInviteAudience =
  | { type: 'organization' }
  | { type: 'team'; teamId: number };

export type PulseSurveyFormSubmitRequest = {
  payload: Partial<PulseSurvey>;
  inviteAudience?: PulseSurveyFormSubmitInviteAudience;
};

type FormState = {
  status: number;
  assessmentQuestionSet?: number;
  startDateTime: Date;
  endDateTime: Date;
  reminderEnabled: boolean;
  reminderSchedule: string;
};

const errorDetail = (err: any, fallback: string) => String(err?.data?.detail || err?.data?.error || fallback);

const PulseSurveyFormModal: React.FC<PulseSurveyFormModalProps> = ({
  open,
  survey,
  questionSets,
  teams = [],
  orgName = 'Organization',
  isSubmitting = false,
  defaultReminderSchedule = DEFAULT_REMINDER_SCHEDULE,
  orgTimezone,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [showReminderEditor, setShowReminderEditor] = useState(false);
  const [inviteAudience, setInviteAudience] = useState<string>(ORG_AUDIENCE_VALUE);
  const isStaffOrSuperAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const currentOrganization = useSelector(getCurrentOrganization) as { is_impersonating?: boolean; isImpersonating?: boolean } | null;
  const impersonationMode = useSelector(getImpersonationMode);
  const isOrgContextImpersonating = Boolean(currentOrganization?.is_impersonating || currentOrganization?.isImpersonating);
  const isImpersonating = impersonationMode === ImpersonationMode.ACTIVE || isOrgContextImpersonating;
  const showAdvancedReminderModes = isStaffOrSuperAdmin && !isImpersonating;

  const defaultStartDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }, []);

  const defaultEndDate = useMemo(() => {
    const d = new Date(defaultStartDate);
    d.setDate(d.getDate() + 7);
    d.setHours(18, 0, 0, 0);
    return d;
  }, [defaultStartDate]);

  const [form, setForm] = useState<FormState>({
    status: 1,
    assessmentQuestionSet: undefined,
    startDateTime: defaultStartDate,
    endDateTime: defaultEndDate,
    reminderEnabled: true,
    reminderSchedule: defaultReminderSchedule,
  });

  useEffect(() => {
    if (!open) return;

    if (survey) {
      setForm({
        status: survey.status,
        assessmentQuestionSet: survey.assessment_question_set,
        startDateTime: new Date(survey.start_date_time),
        endDateTime: new Date(survey.end_date_time),
        reminderEnabled: Boolean(survey.reminder_enabled),
        reminderSchedule: survey.reminder_schedule || defaultReminderSchedule,
      });
      setInviteAudience(ORG_AUDIENCE_VALUE);
    } else {
      setForm({
        status: 1,
        assessmentQuestionSet: questionSets.length > 0 ? Number(questionSets[0].id) : undefined,
        startDateTime: defaultStartDate,
        endDateTime: defaultEndDate,
        reminderEnabled: true,
        reminderSchedule: defaultReminderSchedule,
      });
      setInviteAudience(ORG_AUDIENCE_VALUE);
    }

    setShowReminderEditor(false);
    setError(null);
  }, [
    open,
    survey,
    questionSets,
    defaultStartDate,
    defaultEndDate,
    defaultReminderSchedule,
  ]);

  useEffect(() => {
    if (!open || survey || form.assessmentQuestionSet || questionSets.length === 0) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      assessmentQuestionSet: Number(questionSets[0].id),
    }));
  }, [open, survey, form.assessmentQuestionSet, questionSets]);

  const inviteAudienceOptions = useMemo(() => {
    const sortedTeams = [...teams].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' }));

    return [
      {
        key: ORG_AUDIENCE_VALUE,
        value: ORG_AUDIENCE_VALUE,
        text: `All at ${orgName}`,
      },
      ...sortedTeams.map((team) => ({
        key: `${TEAM_AUDIENCE_PREFIX}${team.id}`,
        value: `${TEAM_AUDIENCE_PREFIX}${team.id}`,
        text: team.name,
      })),
    ];
  }, [teams, orgName]);

  const cronInfo = validateCronExpression(form.reminderSchedule || '', orgTimezone);
  const isDateRangeValid = form.startDateTime < form.endDateTime;

  const save = async () => {
    if (!form.assessmentQuestionSet) {
      setError('No pulse question set is available.');
      return;
    }

    if (!isDateRangeValid) {
      setError('End date must be after start date.');
      return;
    }

    if (form.reminderEnabled && !cronInfo.valid) {
      setError(cronInfo.error || 'Reminder schedule is invalid.');
      return;
    }

    let createAudience: PulseSurveyFormSubmitInviteAudience | undefined;
    if (!survey) {
      if (inviteAudience === ORG_AUDIENCE_VALUE) {
        createAudience = { type: 'organization' };
      } else if (inviteAudience.startsWith(TEAM_AUDIENCE_PREFIX)) {
        const teamId = Number(inviteAudience.slice(TEAM_AUDIENCE_PREFIX.length));
        if (!Number.isFinite(teamId)) {
          setError('Choose a valid invite audience.');
          return;
        }
        createAudience = { type: 'team', teamId };
      } else {
        setError('Choose a valid invite audience.');
        return;
      }
    }

    try {
      setError(null);
      await onSubmit({
        payload: {
          status: form.status,
          team: createAudience?.type === 'team' ? createAudience.teamId : null,
          assessment_question_set: form.assessmentQuestionSet,
          start_date_time: form.startDateTime.toISOString(),
          end_date_time: form.endDateTime.toISOString(),
          reminder_enabled: form.reminderEnabled,
          reminder_schedule: form.reminderSchedule || null,
        },
        inviteAudience: createAudience,
      });
      onClose();
    } catch (err) {
      setError(errorDetail(err, 'Failed to save schedule instance.'));
    }
  };

  return (
    <PositionedModal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>{survey ? 'Pulse Survey Schedule' : 'Create Schedule Instance'}</Modal.Header>
      <Modal.Content>
        <Form>
          {!survey && (
            <FormRow>
              <FieldLabel>Invite</FieldLabel>
              <FieldControl>
                <Dropdown
                  fluid
                  selection
                  options={inviteAudienceOptions}
                  value={inviteAudience}
                  placeholder="Select audience"
                  onChange={(_, data) => setInviteAudience(String(data.value || ORG_AUDIENCE_VALUE))}
                />
              </FieldControl>
            </FormRow>
          )}

          <FormRow>
            <FieldLabel>Start Date</FieldLabel>
            <FieldControl>
              <StyledDateTimeInput
                value={form.startDateTime}
                onChange={(value) => setForm((prev) => ({ ...prev, startDateTime: value || prev.startDateTime }))}
                hasValue={!!form.startDateTime}
              />
            </FieldControl>
          </FormRow>

          <FormRow>
            <FieldLabel>End Date</FieldLabel>
            <FieldControl>
              <StyledDateTimeInput
                value={form.endDateTime}
                onChange={(value) => setForm((prev) => ({ ...prev, endDateTime: value || prev.endDateTime }))}
                hasValue={!!form.endDateTime}
              />
              {!isDateRangeValid && <ErrorText>End date must be after start date.</ErrorText>}
            </FieldControl>
          </FormRow>

          <FormRow>
            <FieldLabel>Reminder Schedule</FieldLabel>
            <FieldControl>
              <ReminderHeaderRow>
                <PlainEnglishText
                  $valid={cronInfo.valid}
                  onClick={() => setShowReminderEditor((prev) => !prev)}
                  title="Click to edit reminder schedule"
                >
                  {!form.reminderEnabled
                    ? 'Automated reminders disabled'
                    : cronInfo.valid
                      ? cronInfo.plainEnglish
                      : cronInfo.error || 'Invalid schedule expression'}
                </PlainEnglishText>

                <ButtonRow>
                  <StyledIconButton
                    icon={form.reminderEnabled ? 'bell' : 'bell slash'}
                    title={form.reminderEnabled ? 'Disable Reminders' : 'Enable Reminders'}
                    color={form.reminderEnabled ? 'green' : 'grey'}
                    onClick={() => setForm((prev) => ({ ...prev, reminderEnabled: !prev.reminderEnabled }))}
                    disabled={false}
                  />
                  <StyledIconButton
                    icon="edit"
                    title={showReminderEditor ? 'Hide Schedule' : 'Edit Schedule'}
                    onClick={() => setShowReminderEditor((prev) => !prev)}
                    disabled={false}
                  />
                </ButtonRow>
              </ReminderHeaderRow>

              {showReminderEditor && (
                <ReminderSection>
                  <CronExpressionInput
                    value={form.reminderSchedule}
                    onChange={(value) => setForm((prev) => ({ ...prev, reminderSchedule: value ?? '' }))}
                    placeholder={DEFAULT_REMINDER_SCHEDULE}
                    timezone={orgTimezone}
                    showAdvancedModes={showAdvancedReminderModes}
                  />
                </ReminderSection>
              )}
            </FieldControl>
          </FormRow>

          {error && <Message negative content={error} />}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button primary onClick={save} loading={isSubmitting} disabled={isSubmitting}>
          {survey ? 'Save Changes' : 'Create'}
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

export default PulseSurveyFormModal;

const FieldControl = styled.div`
  flex: 1;
  min-width: 0;

  & > * {
    width: 100%;
  }
`;

const ErrorText = styled.div`
  color: #db2828;
  font-size: 12px;
  margin-top: 4px;
`;

const ReminderSection = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const ReminderHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
`;

const PlainEnglishText = styled.div<{ $valid: boolean }>`
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;

  ${({ $valid }) => {
    if (!$valid) {
      return `
        color: #c62828;
        background: #ffebee;
        border: 1px solid #ef9a9a;
      `;
    }

    return `
      color: #344054;
      background: #f8fafc;
      border: 1px solid #d0d5dd;
    `;
  }}
`;
